import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import {
  listPlacementSessions,
  getInstitutionCredits,
  allocateCredits,
  adjustCredits,
  listCreditTransactions,
} from "../api/client";

// Credits are pooled per batch: remaining = included − used. Allocation tops up
// a batch's pool; every change is written to an append-only ledger server-side.

function StatCard({ icon, label, value, hint, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span
          className={
            "flex h-8 w-8 items-center justify-center rounded-lg " +
            (accent || "bg-primary/10 text-primary")
          }
        >
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

const TXN_LABEL = {
  allocation: "Allocated",
  reserve: "Reserved",
  release: "Released",
  refund: "Refunded",
  adjustment: "Adjusted",
};

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Add / adjust credits modal ────────────────────────────────────────────────
function CreditModal({ batch, walletBalance = 0, onClose, onDone, onBuy }) {
  const [mode, setMode] = useState("add"); // 'add' | 'adjust'
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const n = parseInt(amount, 10);
    if (Number.isNaN(n)) return setError("Enter a whole number of credits.");
    if (mode === "add" && n <= 0) return setError("Amount must be greater than zero.");
    if (mode === "add" && n > walletBalance)
      return setError(
        `Your wallet has only ${walletBalance} credit${walletBalance === 1 ? "" : "s"}. Buy more before allocating.`
      );
    if (mode === "adjust" && n === 0) return setError("Adjustment can't be zero.");
    if (mode === "adjust" && !reason.trim()) return setError("A reason is required for adjustments.");

    setSaving(true);
    try {
      if (mode === "add") {
        await allocateCredits(batch.id, {
          amount: n,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
      } else {
        await adjustCredits(batch.id, { amount: n, reason: reason.trim() });
      }
      onDone();
    } catch (err) {
      setError(err?.error || "Could not update credits.");
    } finally {
      setSaving(false);
    }
  };

  const remaining = (batch.includedInterviewCredits ?? 0) - (batch.creditsUsed ?? 0);

  return (
    <Modal
      open
      onClose={() => (saving ? null : onClose())}
      title={`Manage credits · ${batch.name}`}
      description={`${remaining} remaining of ${batch.includedInterviewCredits ?? 0} allocated`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={saving}
            iconName="Check"
            disabled={saving || (mode === "add" && walletBalance <= 0)}
          >
            {mode === "add" ? "Add credits" : "Apply adjustment"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {/* mode toggle */}
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1 text-sm">
          <button
            type="button"
            onClick={() => setMode("add")}
            className={
              "rounded-md px-3 py-1.5 font-medium transition-colors " +
              (mode === "add" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")
            }
          >
            Add credits
          </button>
          <button
            type="button"
            onClick={() => setMode("adjust")}
            className={
              "rounded-md px-3 py-1.5 font-medium transition-colors " +
              (mode === "adjust" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")
            }
          >
            Adjust
          </button>
        </div>

        {/* Wallet context — allocation draws from the institution wallet */}
        {mode === "add" &&
          (walletBalance > 0 ? (
            <p className="text-xs text-muted-foreground">
              Allocating from your wallet balance of{" "}
              <span className="font-medium text-foreground">{walletBalance}</span> credit
              {walletBalance === 1 ? "" : "s"}.
            </p>
          ) : (
            <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-warning">
              <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-sm">
                <p className="font-medium">Your wallet is empty.</p>
                <p className="text-xs">Buy credits before allocating them to this batch.</p>
                {onBuy && (
                  <button
                    type="button"
                    onClick={onBuy}
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold underline"
                  >
                    <Icon name="CreditCard" size={12} /> Buy credits
                  </button>
                )}
              </div>
            </div>
          ))}

        <Input
          label={mode === "add" ? "Credits to add" : "Adjustment (+/−)"}
          type="number"
          step="1"
          min={mode === "add" ? "1" : undefined}
          max={mode === "add" ? String(walletBalance) : undefined}
          placeholder={mode === "add" ? "e.g. 100" : "e.g. -10 to remove, 25 to add"}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          description={
            mode === "adjust"
              ? "Use a negative number to remove credits. Can't drop below credits already used."
              : walletBalance > 0
                ? `Draws from your wallet — up to ${walletBalance} available.`
                : "One credit covers one assigned interview."
          }
          autoFocus
        />
        <Input
          label={mode === "add" ? "Reason (optional)" : "Reason"}
          required={mode === "adjust"}
          placeholder="e.g. Semester 1 top-up"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <p className="flex items-center gap-1.5 text-sm text-destructive">
            <Icon name="AlertCircle" size={14} /> {error}
          </p>
        )}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

// ── Ledger history modal ──────────────────────────────────────────────────────
function HistoryModal({ batch, onClose }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await listCreditTransactions(batch.id, { limit: 100 });
        if (alive) setTxns(data.transactions || []);
      } catch (err) {
        if (alive) setError(err?.error || "Could not load history.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [batch.id]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Credit history · ${batch.name}`}
      description="Every change to this batch's credit pool."
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />
          ))}
        </div>
      ) : error ? (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <Icon name="AlertCircle" size={14} /> {error}
        </p>
      ) : txns.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No credit activity yet.
        </div>
      ) : (
        <ul className="max-h-96 divide-y divide-border overflow-y-auto">
          {txns.map((t) => {
            const positive = t.amount > 0;
            return (
              <li key={t.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {TXN_LABEL[t.transactionType] || t.transactionType}
                    {t.reason ? (
                      <span className="font-normal text-muted-foreground"> · {t.reason}</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDateTime(t.createdAt)}</div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-4">
                  <span
                    className={
                      "text-sm font-semibold tabular-nums " +
                      (positive ? "text-success" : "text-destructive")
                    }
                  >
                    {positive ? "+" : ""}
                    {t.amount}
                  </span>
                  {t.balanceAfter != null && (
                    <span className="w-20 text-right text-xs text-muted-foreground tabular-nums">
                      bal {t.balanceAfter}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

function CreditBar({ used, included }) {
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={"h-full rounded-full " + (pct >= 90 ? "bg-destructive" : "bg-primary")}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function Credits() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [creditBatch, setCreditBatch] = useState(null); // batch being topped up
  const [historyBatch, setHistoryBatch] = useState(null); // batch whose ledger is open

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [rows, totals] = await Promise.all([
        listPlacementSessions(),
        getInstitutionCredits(),
      ]);
      setBatches(rows);
      setRollup(totals);
    } catch (err) {
      setLoadError(err?.error || "Could not load credits.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hasBatches = batches.length > 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Credits</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Allocate credits from your wallet to each batch. One credit covers one
            assigned interview.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="CreditCard"
          onClick={() => navigate("/institution/buy-credits")}
        >
          Buy credits
        </Button>
      </div>

      {/* Rollup */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon="Wallet"
          label="Wallet balance"
          value={rollup ? rollup.walletBalance : "…"}
          hint="Purchased, not yet allocated"
          accent="bg-success/15 text-success"
        />
        <StatCard
          icon="Layers"
          label="Available in batches"
          value={rollup ? rollup.creditsRemaining : "…"}
          hint="Allocated and unused"
        />
        <StatCard
          icon="Activity"
          label="Credits in use"
          value={rollup ? rollup.creditsUsed : "…"}
          hint="Reserved by assignments"
        />
        <StatCard
          icon="CheckCircle2"
          label="Total allocated"
          value={rollup ? rollup.includedInterviewCredits : "…"}
          hint={rollup ? `${rollup.batchCount} batch${rollup.batchCount === 1 ? "" : "es"}` : ""}
        />
      </div>

      {/* Load error */}
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* Per-batch pools */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl border border-border bg-muted/40" />
          ))}
        </div>
      ) : !hasBatches && !loadError ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name="Wallet" size={24} />
          </span>
          <h3 className="mt-4 text-base font-semibold text-foreground">No batches to fund yet</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Create a batch first — then you can allocate a credit pool to it.
          </p>
          <Button className="mt-5" iconName="Plus" onClick={() => navigate("/institution/batches")}>
            Go to batches
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Batch</th>
                  <th className="px-4 py-3 font-medium">Remaining</th>
                  <th className="px-4 py-3 font-medium">Used</th>
                  <th className="px-4 py-3 font-medium">Allocated</th>
                  <th className="px-4 py-3 font-medium">Usage</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const included = b.includedInterviewCredits ?? 0;
                  const used = b.creditsUsed ?? 0;
                  const remaining = included - used;
                  return (
                    <tr key={b.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{b.name}</div>
                        {b.academicYear && (
                          <div className="text-xs text-muted-foreground">{b.academicYear}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            "font-semibold tabular-nums " +
                            (remaining === 0 && included > 0 ? "text-destructive" : "text-foreground")
                          }
                        >
                          {remaining}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">{used}</td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">{included}</td>
                      <td className="px-4 py-3">
                        <div className="w-28">
                          <CreditBar used={used} included={included} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Plus"
                            onClick={() => setCreditBatch(b)}
                          >
                            Add
                          </Button>
                          <button
                            onClick={() => setHistoryBatch(b)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            title="History"
                            aria-label={`Credit history for ${b.name}`}
                          >
                            <Icon name="History" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creditBatch && (
        <CreditModal
          batch={creditBatch}
          walletBalance={rollup?.walletBalance ?? 0}
          onClose={() => setCreditBatch(null)}
          onDone={() => {
            setCreditBatch(null);
            load();
          }}
          onBuy={() => navigate("/institution/buy-credits")}
        />
      )}
      {historyBatch && (
        <HistoryModal batch={historyBatch} onClose={() => setHistoryBatch(null)} />
      )}
    </div>
  );
}
